/* eslint-disable max-lines */
import { EditOutlined } from '@ant-design/icons'
import { Col, Divider, Grid, Row } from 'antd'
import ConfirmationButtons from 'app/components/Core/Checkout/ConfirmationButtons'
import useDeliveryContactForm from 'app/components/Core/Checkout/hooks/useDeliveryContactForm'
import useLineItemUpdate from 'app/components/Core/Checkout/hooks/useLineItemUpdate'
import useMachineSearch from 'app/components/Core/Checkout/hooks/useMachineSearch'
import useManagerContactForm from 'app/components/Core/Checkout/hooks/useManagerContactForm'
import { useProductIndex } from 'app/components/Core/Checkout/hooks/useProduct'
import ContactDetail from 'app/components/Core/Contact/Detail'
import DeliveryAddressListItem from 'app/components/Core/DeliveryAddress/ListItem'
import LineItemDetailItem from 'app/components/Core/LineItem/Item/Detail'
import useDeliveryAddressForm from 'app/components/Core/Rental/hooks/useDeliveryAddressForm'
import KHelp from 'app/components/Modules/KHelp'
import Button from 'app/components/UI-Elements/General/Button'
import Restricted from 'app/components/UI-Elements/General/Restricted'
import Container from 'app/components/UI-Elements/Layout/Container'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import KFooter from 'app/components/UI-Elements/Layout/KFooter'
import { RecordType } from 'app/constants/GlobalTypes'
import rentalNormalizer from 'app/lib/apollo/normalizers/Rental'
import { useBrowserRouterContext } from 'app/lib/contexts/KBrowserRouter'
import { useQuery } from 'app/lib/hooks/useQuery'
import useSetBackHistory from 'app/lib/hooks/useSetBackHistory'
import useTitle from 'app/lib/hooks/useTitle'
import useTranslation from 'app/lib/hooks/useTranslation'
import { PageProps } from 'app/pages/typing'
import { RecordTypeEnum } from 'app/queries'
import * as rentalsCheckout from 'app/queries/Rentals/Checkout'
import { Routes } from 'app/routes/Routes'
import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import CheckoutStepper from '../Component/Stepper'
import RentalCheckoutPrices from '../Container/Prices'

const Confirmation: React.FC<PageProps> = (props) => {
  useTitle(props)
  const { tRecordType, tRoute, tAttribute } = useTranslation({
    scope: props.route.key,
    recordType: RecordTypeEnum.rental
  })

  const { checkout_id: rental_id } = useParams<{ checkout_id: string }>()
  const { setBackRoute } = useBrowserRouterContext()
  const {
    data: { rental },
    loading,
    refetch: onSuccess
  } = useQuery<{ rental: IRental }>(rentalsCheckout.RENTAL_CHECKOUT_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: rental_id
    },
    normalizer: (data) => ({ rental: rentalNormalizer(data.rental) })
  })
  const { open: openLineItemUpdate } = useLineItemUpdate()
  const { open: openMachineSearch } = useMachineSearch()
  const managerContact = useManagerContactForm()
  const deliveryContact = useDeliveryContactForm()
  const { md } = Grid.useBreakpoint()
  const { data, loading: lineItemsLoading } = useProductIndex({ id: rental_id })
  const { open: openAddressForm } = useDeliveryAddressForm(rental_id)

  const hasNoMachine = useMemo(() => data.length === 0, [data])

  useSetBackHistory(() => {
    setBackRoute(Routes.Checkout.member(rental_id).delivery)
  })

  const onEdit = (item: ILineItem) => {
    openLineItemUpdate({ lineItemId: item.id, onSuccess })
  }

  const onAddressEdit = () => {
    openAddressForm({
      member: rental,
      props: {
        formProps: { onSuccess },
        addressInputProps: { showAvailabilityHint: true }
      }
    })
  }

  const onDeliveryContactEdit = () => {
    deliveryContact.open({
      member: rental
    })
  }

  const onManagerContactEdit = () =>
    managerContact.open({
      member: rental
    })

  const showPrices = !hasNoMachine && rental?.directBookable

  return (
    <Container card={false}>
      <Row gutter={[{ md: 24, xs: 0 }, 0]}>
        <Col xs={24} md={showPrices ? 16 : 24}>
          <Container>
            <Row gutter={[0, 0]}>
              <KCol span={24}>
                <CheckoutStepper activeStep={3} />
              </KCol>
              <KCol span={24} loading={lineItemsLoading}>
                {data.map((item) => (
                  <LineItemDetailItem
                    key={item.id}
                    member={item}
                    props={{
                      onClick: () => onEdit(item),
                      showPrice: rental?.directBookable
                    }}
                  />
                ))}
                <Divider />
              </KCol>
              <If condition={hasNoMachine && !lineItemsLoading}>
                <KCol span={24} className="text-center">
                  <p className="text-gray mb-0">{tRoute({ id: 'all_machines_deleted' })}</p>
                </KCol>
                <KCol span={24} className="text-center">
                  <Button
                    onClick={() => openMachineSearch()}
                    type="secondary"
                    title={tRoute({ id: 'add_machine' })}
                  />
                </KCol>
              </If>
              <If condition={showPrices && !md && !loading}>
                <KCol span={24} loading={loading}>
                  <RentalCheckoutPrices />
                </KCol>
              </If>
              <KCol span={24} className="pb-4">
                <Row gutter={[0, 12]}>
                  <KCol span={24}>
                    <h6 className="mb-0 text-black">{tAttribute({ id: 'addressSummary' })}</h6>
                  </KCol>
                  <KCol span={24} loading={loading}>
                    <DeliveryAddressListItem
                      member={rental?.deliveryAddress}
                      props={{ onEditClick: onAddressEdit }}
                    />
                  </KCol>
                  <KCol span={24}>
                    <h6 className="mb-0 text-black">{tRecordType({ id: 'delivery_contact' })}</h6>
                  </KCol>
                  <KCol span={24} loading={loading}>
                    <ContactDetail
                      props={{
                        extra: (
                          <Button
                            onClick={onDeliveryContactEdit}
                            type="secondary"
                            icon={<EditOutlined />}
                          />
                        )
                      }}
                      member={rental?.deliveryContact}
                    />
                  </KCol>
                  <Restricted allowFor={['member']}>
                    <>
                      <KCol span={24}>
                        <h6 className="mb-0 text-black">
                          {tRecordType({ id: 'manager_contact' })}
                        </h6>
                      </KCol>
                      <KCol span={24} loading={loading}>
                        <ContactDetail
                          props={{
                            description: rental?.managerContact?.email,
                            extra: (
                              <Button
                                onClick={onManagerContactEdit}
                                type="secondary"
                                icon={<EditOutlined />}
                              />
                            )
                          }}
                          member={rental?.managerContact}
                        />
                      </KCol>
                    </>
                  </Restricted>
                </Row>
              </KCol>
              <KCol span={24} className="py-2" hidden={!rental?.directBookable}>
                <KHelp recordType={RecordType.checkout} id="direct_booking" />
              </KCol>
            </Row>
            <KFooter
              sticky={!md}
              style={{ padding: md ? '16px 0 0 0' : undefined }}
              colProps={{ span: !md ? 24 : undefined }}>
              <ConfirmationButtons
                member={rental}
                props={{ loading: loading || lineItemsLoading, disabled: hasNoMachine }}
              />
            </KFooter>
          </Container>
        </Col>
        <If condition={showPrices && md}>
          <KCol xs={8} loading={loading}>
            <Container>
              <RentalCheckoutPrices />
            </Container>
          </KCol>
        </If>
      </Row>
    </Container>
  )
}

export default Confirmation
